<template>
  <div class="address-form">
    <ErrorList v-if="user.hasErrors" :errors="user.errors" />
    <PapaAutoForm v-if="ready" v-model:model-value="formData" :fields="fields">
      <template #default>
        <!-- empty -->
        <span />
      </template>
    </PapaAutoForm>
  </div>
</template>

<script setup lang="ts">
import PapaAutoForm from "@/components/PapaAutoForm.vue";
import { useAddressStore } from "@/stores/address";
import { useUserStore } from "@/stores/user";
import type { Address } from "@paparazzi/types/paparazzi.api.serializers";
import copy from "@virgodev/bazaar/functions/copy";
import ErrorList from "@virgodev/vue-models/components/forms/ErrorList.vue";
import type { FieldDef } from "@virgodev/vue-models/components/forms/defs";
import { computed, nextTick, onMounted, ref, watch, watchEffect } from "vue";

const addresses = useAddressStore();
const user = useUserStore();

const props = withDefaults(
  defineProps<{
    modelValue: Partial<Address>;
    includeName?: boolean;
    shipping?: boolean;
    billing?: boolean;
  }>(),
  {
    includeName: false,
    shipping: true,
    billing: true,
  },
);
const emits = defineEmits(["update:modelValue"]);
// const model = defineModel<Address>()

const ready = ref(false);

const formData = computed({
  get: () => {
    return props.modelValue;
  },
  set: (v) => {
    // console.log("new value", v)
    emits("update:modelValue", copy(v));
  },
});

watch(
  () => props.modelValue?.zip_code,
  async () => {
    await addresses.fetchCountyInfo(props.modelValue?.zip_code || "", {
      existing: {
        city: props.modelValue.city,
        state: props.modelValue.state,
        county: props.modelValue.county,
      },
    });

    if (ready.value) {
      const address = formData.value;
      for (const key in addresses.countyOptions) {
        const options = addresses.countyOptions[key];
        if (options.length > 0) {
          address[key] = options[0];
        }
      }
    }
  },
);

watchEffect(() => {
  if (props.modelValue.default_billing) {
    addresses.list
      .filter((a) => a.id !== props.modelValue.id)
      .forEach((a) => (a.default_billing = false));
  }
  if (props.modelValue.default_shipping) {
    addresses.list
      .filter((a) => a.id !== props.modelValue.id)
      .forEach((a) => (a.default_shipping = false));
  }
});

const fields = computed(() => {
  let retval = [] as FieldDef[];
  if (props.includeName) {
    retval.push({ name: "name" });
  }
  retval = retval.concat(addresses.fields);
  if (addresses.list.length > 0) {
    if (props.shipping) {
      retval.push({
        name: "default_shipping",
        type: "checkbox",
        label: "Default",
      });
    }
  }
  return retval;
});

onMounted(async () => {
  addresses.reset();
  addresses.allowPOBoxes = true;
  if (props.modelValue?.zip_code) {
    await addresses.fetchCountyInfo(props.modelValue.zip_code, {
      existing: {
        city: props.modelValue.city,
        state: props.modelValue.state,
        county: props.modelValue.county,
      },
    });
  }

  await nextTick();
  ready.value = true;
});
</script>

<style scoped>
.address-form :deep(label.checkbox) {
  font-weight: 400 !important;
  text-transform: capitalize !important;
  line-height: 1 !important;
}
</style>
